import { useQuery } from '@tanstack/react-query'
import axiosStatic from 'axios'
import { useEffect, useMemo } from 'react'

import {
  PaginatedResourceResponse,
  UsePaginatedQuery,
  UsePaginatedQueryParams,
} from './use-paginated-query'

const usePaginatedQuery = <Resource, CustomData = unknown>({
  ...props
}: UsePaginatedQueryParams<Resource, CustomData>): UsePaginatedQuery<
  Resource,
  CustomData
> => {
  const {
    url,
    queryIdentifier,
    customHeaders,
    axios = axiosStatic,
    page,
    perPage,
    sort,
    filters,
    queryOptions = {},
    setExternalQueryKey,
    setExternalRequestBody,
  } = props

  const requestPaginatedResource = async (): Promise<
    PaginatedResourceResponse<Resource, CustomData>
  > => {
    const { data } = await axios.post<
      PaginatedResourceResponse<Resource, CustomData>
    >(url, {
      page,
      sort,
      filter: filters,
      customHeaders,
      perPage,
    })

    return data
  }

  const queryKey = useMemo(
    () => [queryIdentifier, page, perPage, sort, filters],
    [queryIdentifier, page, perPage, sort, filters],
  )

  useEffect(() => {
    if (typeof setExternalQueryKey === 'function') {
      setExternalQueryKey(queryKey)
    }
  }, [queryKey, setExternalQueryKey])

  useEffect(() => {
    if (typeof setExternalRequestBody === 'function') {
      setExternalRequestBody({
        page,
        sort,
        filter: filters,
        customHeaders,
        perPage,
      })
    }
  }, [page, sort, filters, customHeaders, perPage, setExternalRequestBody])

  const paginatedQuery = useQuery<
    PaginatedResourceResponse<Resource, CustomData>
  >({
    queryKey,
    queryFn: requestPaginatedResource,
    placeholderData: (pv) =>
      pv ?? {
        data: [],
        meta: {
          total: 0,
          current_page: page,
          per_page: perPage,
          from: 0,
          to: 0,
          last_page: 0,
          links: [],
          path: url,
        },
        links: {
          first: '',
          last: '',
          prev: '',
          next: '',
        },
      },

    ...queryOptions,
  })

  return paginatedQuery
}

export default usePaginatedQuery
